/* eslint-disable no-useless-escape */
import { PhoneType } from '@/domain/models/person'
import { AddressPageType, AddressOrder } from '../../address'
import { AgreementProduct } from '../../agreement/agreement-product'
import { AttachmentType } from '../../attachments/attachments'
import { DigitalStoreTermsEnum } from '../../digital-store/digital-store-terms-type'
import { ExtraEntry } from '../../digital-store/extra-entry'
import { Document } from '../../documents/documents'
import { Link } from '../../link'
import { VendorId } from '../../vendor/vendor-id'
import {
  AddressConfigs,
  AttachmentsConfigs,
  AuthorizationCodeConfigs,
  CompletedConfigs,
  DigitalSignaturePageConfigs,
  DigitalStoreConfigs,
  DocumentationConfigs,
  MaintenancePersonalDataConfigs,
  MaintenanceRegisterConfigs,
  PagesTenantConfigs,
  PersonalDataConfigs,
  PreRegisterConfigs,
  ProfileConfigs,
} from './configs.d'
import {
  regexToValidateAllSpecialNumberCharactersForMexico,
  regexToValidateAllSpecialStreetCharactersForMexico,
  regexToValidateCommonSpecialCharactersForMexico,
} from './helpers'

const personalData: PersonalDataConfigs = {
  recommender: {
    shouldDisplay: ({ authenticated }) => shouldRenderRecommenderField(authenticated),
  },
  shouldUsePhoneSelect: false,
  showAsteriskInRequiredLabel: false,
  phones: [
    {
      type: PhoneType.CELLULAR,
      mask: [
        /[0-9]/,
        /[0-9]/,
        ' ',
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        ' ',
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
      ],
      length: 10,
    },
  ],
  shouldUseSeparatedNames: true,
  namesOptions: ['firstName', 'middleName', 'lastName'],
  allocationStrategy: {
    searchAddress: {},
    searchAddressWithZipCode: {
      zipCode: {
        rules: {
          required: true,
          maxLength: 5,
          minLength: 5,
          pattern: new RegExp(/^\d{0,5}$/),
        },
        validChars: new RegExp(/^\d{0,5}$/),
        shouldDisplayLink: () => false,
      },
      city: {
        validChars: regexToValidateCommonSpecialCharactersForMexico,
        rules: {
          required: true,
          maxLength: 150,
        },
      },
      cologne: {
        validChars: regexToValidateCommonSpecialCharactersForMexico,
        rules: {
          required: true,
          maxLength: 150,
        },
      },
      complement: {
        validChars: new RegExp(/^[A-Za-z0-9. ]{0,7}$/),
        rules: {
          required: false,
          maxLength: 7,
        },
      },
      number: {
        validChars: regexToValidateAllSpecialNumberCharactersForMexico,
        rules: {
          required: true,
          maxLength: 8,
        },
        hasCheckboxWithoutAddressNumber: true,
      },
      reference: {
        validChars: regexToValidateCommonSpecialCharactersForMexico,
        rules: {
          required: true,
          maxLength: 40,
        },
      },
      state: {
        validChars: regexToValidateCommonSpecialCharactersForMexico,
        rules: {
          required: true,
          maxLength: 150,
        },
      },
      street: {
        validChars: regexToValidateAllSpecialStreetCharactersForMexico,
        rules: {
          required: true,
          maxLength: 24,
        },
      },
    },
  },
}

export const shouldRenderRecommenderField = (authenticated) => {
  if (process.env.SHOULD_HIDE_RECOMMENDER_FIELD_ON_AUTHENTICATED_FLOW === 'true') {
    return !authenticated
  }

  return true
}

export const preRegister: PreRegisterConfigs = {
  documents: [
    {
      type: Document.Curp,
      name: 'curp',
      validChars: /^[\w˜Ñ]{0,18}$/m,
      isUpperCase: true,
      rules: {
        pattern: /^([A-Z][AEIOUX][A-Z]{2}[0-9]{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12][0-9]|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z0-9])([0-9])$/,
        minLength: 18,
        maxLength: 18,
      },
    },
  ],
  link: {
    href: Link.consultCurp,
  },
}

const attachments: AttachmentsConfigs = {
  documents: [
    {
      type: Document.PassportMexico,
      name: 'Pasaporte',
      attachments: [{ title: '', type: AttachmentType.PassportMexico }],
    },
    {
      name: 'INE',
      attachments: [
        { title: 'Frente', type: AttachmentType.IneFront },
        { title: 'Dorso', type: AttachmentType.IneBack },
      ],
    },
  ],
  canChooseDocument: true,
}

const digitalStore: DigitalStoreConfigs = {
  showSkipButton: true,
  showEmailInfoRegistration: true,
  acceptTermsStrategy: DigitalStoreTermsEnum.DEFAULT,
  vendorId: VendorId.EMPTY,
  agreement: {
    products: [AgreementProduct.DIGITAL_STORE],
  },
  extraEntries: [
    {
      type: ExtraEntry.Clabe,
      name: 'clabe',
      mask: [
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
      ],
      isUpperCase: true,
      required: true,
      rules: {
        pattern: /^[0-9]{18}/,
        minLength: 18,
        maxLength: 18,
      },
    },
    {
      type: ExtraEntry.Rfc,
      name: 'rfc',
      mask: [
        /[a-zA-Z]/,
        /[a-zA-Z]/,
        /[a-zA-Z]/,
        /[a-zA-Z]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[0-9]/,
        /[a-zA-Z0-9]/,
        /[a-zA-Z0-9]/,
        /[a-zA-Z0-9]/,
      ],
      isUpperCase: true,
      required: true,
      rules: {
        pattern: /^[A-Z]{4}[0-9]{6}[a-zA-Z0-9]{3}/,
        minLength: 13,
        maxLength: 13,
      },
    },
  ],
  showWarningAfterGetSubmitError: true,
  digitalStoreNameMaxOfChars: 20,
  shouldUnlinkAgreements: false,
  digitalStoreTryCreateAgain: true,
}

const address: AddressConfigs = {
  pageType: AddressPageType.SearchedAddress,
  shouldDisableTheFilledInput: true,
  zipCode: {
    rules: {
      maxLength: 5,
      pattern: new RegExp(/^\d{0,5}$/),
    },
    shouldDisplayLink: () => false,
  },
  reference: {
    maxLength: 40,
  },
  complement: {
    pattern: new RegExp(/^[A-Za-z0-9. ]*$/),
  },
  validateSpecialCharacters: new RegExp(/^[a-zA-Z0-9\\\-\\\(\\)\\#\\_\\\/\\:\\,\\. ]*$/),
  validateAllSpecialCharacters: new RegExp(/^[a-zA-Z0-9áéíóúÁÉÍÓÚ'\\\-\\\(\\)\\#\\_\\\/\\:\\,\\. ]*$/),
  fieldsOrder: [
    AddressOrder.STREET,
    AddressOrder.NUMBER,
    AddressOrder.COMPLEMENT,
    AddressOrder.REFERENCES,
    AddressOrder.COLOGNE,
    AddressOrder.CITY,
    AddressOrder.STATE,
  ]
}

const documentation: DocumentationConfigs = {
  agreementProducts: [AgreementProduct.AVON_PRIVACY_POLICY],
  shouldHaveBirthState: true,
}

const digitalSignaturePage: DigitalSignaturePageConfigs = {
  agreement: {
    previewContract: AgreementProduct.RESELLER,
    openedCheckboxDialog: {
      first: false,
      second: true,
    },
    products: [
      {
        name: AgreementProduct.BUREAU,
        checkboxOrder: 'second',
      },
      {
        name: AgreementProduct.RESELLER,
        checkboxOrder: 'first',
      },
    ],
  },
}

const completed: CompletedConfigs = {
  rejected: {
    hasEmailContact: false,
  },
  shouldSearchIndication: false,
  shouldCheckStatus: false,
  shouldOnlyShowConfirmation: true,
  shouldShowIndicativePerson: true,
  shouldShowContactInfos: false,
  shouldShowConfirmationDescription: true,
  goToClickButton: {
    show: true,
    path: `${process.env.LANDING_PAGE_URL}/mx/quiero-ser-consultora`,
  },
}

const authorizationCode: AuthorizationCodeConfigs = {
  phoneNumberHyphenIndex: 2,
}

const maintenanceRegisterPage: MaintenanceRegisterConfigs = null

const maintenancePersonalDataPage: MaintenancePersonalDataConfigs = null

const profile: ProfileConfigs = null

export const avonMexConfigs: PagesTenantConfigs = {
  documentation,
  personalData,
  attachments,
  preRegister,
  digitalStore,
  address,
  digitalSignaturePage,
  completed,
  authorizationCode,
  maintenanceRegisterPage,
  maintenancePersonalDataPage,
  profile,
}
